/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import * as Sentry from "@sentry/react";
import type { ProductData } from "@/components/common/HeaderComponents/types";
import type { ProductGroup } from "@/pages/support/TechnicalSupportComponents/types";

/**
 * The order the business wants categories shown in. Moved here from HomePage.tsx
 * so one list serves the homepage and all four support forms.
 *
 * These are the English source values; a translated page falls through to
 * alphabetical order. See IND-36.
 */
export const CATEGORY_DISPLAY_ORDER: readonly string[] = [
  "Speech & Text",
  "Productivity",
  "Screen Recording",
  "Video Surveillance",
  "FTP Tools",
];

type ApiProduct = ProductData[string][number];

/**
 * Safety net only. ProductHeaderView already drops inactive rows; repeating the
 * check here means a future backend change cannot silently put an inactive
 * product in front of a customer. A missing value counts as active, because the
 * field is optional on the shared type.
 */
export function isActiveProduct(product: ApiProduct): boolean {
  return product.product_availability?.toLowerCase() !== "inactive";
}

/**
 * Turns the API's { category: product[] } object into the ProductGroup[] shape
 * the dropdowns already understand.
 *
 * Ordering: the curated CATEGORY_DISPLAY_ORDER first — one shared list, also
 * used by the homepage — then anything it does not name, sorted with the page's
 * own language rules. Products are always sorted by name inside a group.
 * See V-2 and IND-36.
 *
 * Blank category: product_short_catagory is optional in Django (blank=True), so
 * the API can return a group keyed by an empty string. Those products are kept,
 * placed last, and rendered without a heading rather than under a blank one.
 * See IND-4.
 */
export function toProductGroups(
  headerData: ProductData,
  language: string,
): ProductGroup[] {
  const collator = new Intl.Collator(language || "en", {
    sensitivity: "base",
  });

  return Object.entries(headerData)
    .map(([category, products]) => ({
      group: category.trim(),
      items: products
        .filter(isActiveProduct)
        .map((product) => product.product_name)
        .sort((a, b) => collator.compare(a, b)),
    }))
    .filter((group) => group.items.length > 0)
    .sort((a, b) => {
      if (!a.group) return 1; // unnamed group always last
      if (!b.group) return -1;
      const ia = CATEGORY_DISPLAY_ORDER.indexOf(a.group);
      const ib = CATEGORY_DISPLAY_ORDER.indexOf(b.group);
      // Both curated: keep the business order.
      if (ia !== -1 && ib !== -1) return ia - ib;
      // One curated: it comes first.
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      // Neither: alphabetical in the visitor's own language. This is the path
      // every translated page takes, because the curated names are English and
      // the API returns translated ones. See IND-36.
      return collator.compare(a.group, b.group);
    });
}

/** Every product name on the page, flattened. Used for validation and prefill. */
export function flattenProductNames(groups: ProductGroup[]): string[] {
  return groups.flatMap((group) => group.items);
}

/**
 * Removes named products from every group and drops groups left empty.
 *
 * A Set rather than array.includes: the exclusion list would otherwise be walked
 * again for every product, on every change of issue type.
 */
export function excludeProductsFromGroups(
  groups: ProductGroup[],
  excludedNames: readonly string[],
): ProductGroup[] {
  const excluded = new Set(excludedNames);
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !excluded.has(item)),
    }))
    .filter((group) => group.items.length > 0);
}

/** Keeps only the named products. Used by Lost License Key — see V-3. */
export function keepProductsInGroups(
  groups: ProductGroup[],
  allowedNames: readonly string[],
): ProductGroup[] {
  const allowed = new Set(allowedNames);
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => allowed.has(item)),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Maps every product slug to its product name, for URL prefill (V-5).
 *
 * product_slug is the right key, and product-header/ already publishes it, so
 * this needs no backend change at all.
 * - Unique in Django: one slug can only ever mean one product.
 * - It is the slug a product page address is built from (Header.tsx), so a link
 *   written from a page address simply works.
 * - product_name_slug is NOT unique and must not be used here. See IND-35.
 */
export function toSlugNameMap(headerData: ProductData): Record<string, string> {
  const map: Record<string, string> = {};
  Object.values(headerData).forEach((products) => {
    products.filter(isActiveProduct).forEach((product) => {
      if (product.product_slug)
        map[product.product_slug] = product.product_name;
    });
  });
  return map;
}

/**
 * Every active product name for which `predicate` holds.
 *
 * IND-7: this replaces the three hand-typed name lists the support area used to
 * carry. Django now publishes the two facts they encoded — `issues_license_key`
 * and `pricing_type` — so an admin rename can no longer break a rule, because
 * no rule is keyed on a name any more.
 */
function namesWhere(
  headerData: ProductData,
  predicate: (product: ApiProduct) => boolean,
): string[] {
  const names = new Set<string>();
  Object.values(headerData).forEach((products) => {
    products
      .filter(isActiveProduct)
      .filter(predicate)
      .forEach((product) => names.add(product.product_name));
  });
  return [...names];
}

/** Active products the administrator has marked as issuing a licence key. */
export function toLicensedProductNames(headerData: ProductData): string[] {
  return namesWhere(
    headerData,
    (product) => product.issues_license_key === true,
  );
}

/**
 * Active products NOT offered for licence, upgrade or billing questions.
 *
 * A missing flag counts as offered, which is what an older cached response
 * means: nothing disappears from a form because the data is a few minutes old.
 */
export function toUnlicensedProductNames(headerData: ProductData): string[] {
  return namesWhere(
    headerData,
    (product) => product.offer_in_technical_support === false,
  );
}

/** Active products NOT offered for refund questions. */
export function toNonRefundableProductNames(headerData: ProductData): string[] {
  return namesWhere(
    headerData,
    (product) => product.offer_in_billing_support === false,
  );
}

/**
 * Session 423 / F-13 — whether the header Products menu offers this product.
 *
 * The one place the question is answered, read by the three components that
 * draw a Products menu. It replaces HEADER_HIDDEN_PRODUCTS, a list of two
 * product names those three components each read separately.
 *
 * A missing value means SHOWN. That is the model's default, and the comparison
 * is against `false` rather than truthiness on purpose: a catalogue response
 * cached before the field existed must not empty the menu.
 */
export function isShownInHeaderMenu(product: ApiProduct): boolean {
  return product.show_in_header_menu !== false;
}

/**
 * Active products the header Products menu does not offer.
 *
 * Derived from the catalogue rather than restated, so the conversion-reporting
 * check below cannot disagree with the menu itself.
 */
export function toHeaderHiddenProductNames(headerData: ProductData): string[] {
  return namesWhere(headerData, (product) => !isShownInHeaderMenu(product));
}

/**
 * Reports names or slugs the live catalogue no longer contains (IND-6).
 *
 * Reported, never thrown: a mismatch must not stop a page from working. Two
 * lists still need this. The three curated product lists that used to need it
 * are gone, replaced by flags an administrator sets (PR-1), so they cannot
 * drift any more.
 */
export function reportUnknownProductNames(
  liveNames: readonly string[],
  curated: readonly string[],
  listName: string,
): void {
  if (liveNames.length === 0) return;
  const live = new Set(liveNames);
  const missing = curated.filter((name) => !live.has(name));
  if (missing.length === 0) return;
  Sentry.captureMessage(
    `${listName} entries not found in catalogue: ${missing.join(", ")}`,
    "warning",
  );
}

/**
 * Reports a product page the static slug list does not know about (IND-39).
 *
 * src/data/productCatalog.ts feeds two things that must not disagree: the
 * sitemap at build time, and advertising conversion reporting at run time. It
 * cannot be built from the catalogue, because the sitemap plugin runs inside
 * vite.config.ts and a build must not depend on a running backend. So it is
 * checked instead: publish a product page and forget that file, and the page is
 * invisible to search engines and to every campaign, with nothing failing and
 * nothing logged. The cost is unattributed advertising spend.
 *
 * `hiddenNames` excludes products the header does not link to — they have no
 * page, so their absence from the static list is correct, not a gap.
 */
export function reportUnlistedProductSlugs(
  headerData: ProductData,
  staticSlugs: readonly string[],
  hiddenNames: readonly string[],
): void {
  const hidden = new Set(hiddenNames);
  const known = new Set(staticSlugs);
  const missing: string[] = [];
  Object.values(headerData).forEach((products) => {
    products
      .filter(isActiveProduct)
      .filter((product) => !hidden.has(product.product_name))
      .forEach((product) => {
        if (product.product_slug && !known.has(product.product_slug)) {
          missing.push(product.product_slug);
        }
      });
  });
  if (missing.length === 0) return;
  Sentry.captureMessage(
    `Product pages missing from the static slug list, so they report no ` +
      `advertising conversions: ${[...new Set(missing)].join(", ")}`,
    "warning",
  );
}
